import { ThunkDispatch } from "core-integration/src/store";
import { getSquadMode } from "core-integration/src/store/squad/reducers";
import { createEntry } from "core-integration/src/store/squad/thunks";
import {
  IAutopickPreferences,
  ICreateEntryData,
} from "core-integration/src/store/squad/types";
import useIsOneTrustOpen from "plos/src/hooks/useIsOneTrustOpen";
import useIsPLOnboardingOpen from "plos/src/hooks/useIsPLOnboardingOpen";
import { useLocalizedNavigate } from "plos/src/hooks/useLocalizedNavigate";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AutoPickChoice,
  EntryDetailsFormData,
  OnboardingSurface,
} from "../../components/squad/Onboarding/types";
import { useTrackingContext } from "../TrackingContext";

export type LocationState = {
  isNewEntry?: boolean;
};

interface OnboardingContextType {
  isOpen: boolean;
  isTutorialEnabled: boolean;
  activeSurface: OnboardingSurface;
  goToAutopickChoice: () => void;
  goToWizard: () => void;
  handleClose: () => void;
  entryDetails: EntryDetailsFormData | null;
  setEntryDetails: (details: EntryDetailsFormData) => void;
  updateEntryName: (name: string) => void;
  updateEntryDetails: (partial: Partial<EntryDetailsFormData>) => void;
  autopickChoice: AutoPickChoice;
  setAutopickChoice: (choice: AutoPickChoice) => void;
  lastAutopickPreferences: IAutopickPreferences | null;
  setLastAutopickPreferences: (prefs: IAutopickPreferences) => void;
  submitEntry: () => Promise<void>;
  reopenForTutorial: () => void;
}

export const OnboardingContext = createContext<
  OnboardingContextType | undefined
>(undefined);

interface OnboardingProviderProps {
  children: ReactNode;
}

export const OnboardingProvider = ({ children }: OnboardingProviderProps) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const { fireClickTrackEvent, logBranchEvent } = useTrackingContext();
  const [isOpen, setIsOpen] = useState(false);
  const mode = useSelector(getSquadMode);
  const navigate = useLocalizedNavigate();

  // Don't show the onboarding until the OneTrust modal is closed,
  // the PL Onboarding screen is closed (if required),
  // and only show in squad selection mode
  const isOneTrustOpen = useIsOneTrustOpen();
  const isPLOnboardingOpen = useIsPLOnboardingOpen();

  useEffect(() => {
    setIsOpen(mode === "selection" && !isOneTrustOpen && !isPLOnboardingOpen);
  }, [mode, isOneTrustOpen, isPLOnboardingOpen]);

  const isTutorialEnabled =
    import.meta.env.VITE_ONBOARDING_TUTORIAL_ENABLED !== "false";

  // Which surface of the onboarding flow is active.
  const [activeSurface, setActiveSurface] =
    useState<OnboardingSurface>("entryDetails");
  // Entry details persisted between the entry form submit and the final
  // createEntry (dispatched from the squad-page Enter Squad button).
  const [entryDetails, setEntryDetailsState] =
    useState<EntryDetailsFormData | null>(null);
  // Persistent routing choice, read on the squad page (ManageSquad / MyTeam) to
  // drive the "Auto Pick Again" / what's-next behaviour.
  const [autopickChoice, setAutopickChoice] =
    useState<AutoPickChoice>("manual_pick");
  // Preferences from the last wizard run, reused by "Auto Pick Again" so the
  // backend receives the same club/spend/attack preferences.
  const [lastAutopickPreferences, setLastAutopickPreferences] =
    useState<IAutopickPreferences | null>(null);

  const goToAutopickChoice = () => setActiveSurface("autopickChoice");
  const goToWizard = () => setActiveSurface("wizard");
  const resetSurface = () => setActiveSurface("entryDetails");

  const setEntryDetails = (details: EntryDetailsFormData) =>
    setEntryDetailsState(details);
  const updateEntryName = (name: string) =>
    setEntryDetailsState((prev) => (prev ? { ...prev, name } : prev));
  const updateEntryDetails = (partial: Partial<EntryDetailsFormData>) =>
    setEntryDetailsState((prev) => (prev ? { ...prev, ...partial } : prev));

  const handleClose = () => {
    setIsOpen(false);
    resetSurface();
  };

  // Re-open the survey straight into the tutorial wizard, used by the squad
  // page's "Auto Pick With Tutorial" action.
  const reopenForTutorial = () => {
    setAutopickChoice("show_tutorial");
    setActiveSurface("wizard");
    setIsOpen(true);
  };

  // Submits the persisted entry details (with the squad already proposed in
  // Redux) as a new entry.
  const submitEntry = async () => {
    if (!entryDetails) return;

    const favouriteTeam = entryDetails.favourite_team;
    const entryData: ICreateEntryData = {
      email: false,
      favourite_team:
        favouriteTeam == null || Number(favouriteTeam) === -1
          ? null
          : Number(favouriteTeam),
      name: entryDetails.name,
      terms_agreed: entryDetails.terms_agreed,
    };

    try {
      const teamCreated = await dispatch(createEntry(entryData));
      const teamCreatedEvent = {
        event_category: "fantasy team",
        event_component: "fantasy classic clicks",
        event_detail: "submit team",
        event_type: "create team",
      };
      fireClickTrackEvent(teamCreatedEvent, "fantasy squad selection");
      if (teamCreated) {
        logBranchEvent("FPL Classic Team Creation", teamCreatedEvent);
      }
      navigate("/my-team", {
        state: { isNewEntry: true } satisfies LocationState,
      });
    } catch (error) {
      window.console.error("Failed to create entry", error);
    }
  };

  const value = {
    isOpen,
    isTutorialEnabled,
    activeSurface,
    goToAutopickChoice,
    goToWizard,
    handleClose,
    entryDetails,
    setEntryDetails,
    updateEntryName,
    updateEntryDetails,
    autopickChoice,
    setAutopickChoice,
    lastAutopickPreferences,
    setLastAutopickPreferences,
    submitEntry,
    reopenForTutorial,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboardingContext = () => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error(
      "useOnboardingContext must be used within OnboardingProvider",
    );
  }
  return context;
};
